import React, {useCallback, useEffect} from 'react';
import {observer} from 'mobx-react-lite';
import intl from 'react-intl-universal';
import {useAppState} from "@stores/AppStateContext";

import {MODALS} from '../modal-names';
import {AuthModal, AuthModalHeader} from "./AuthModal";
import {Button} from "../../../components/Button";

import {inAppEventsHub} from "../../../../volatile/InAppEventsHub";

export type LockReason = 'confirm_resend_limit' | 'pwd_reset_resend_limit' | 'login_limit';

export interface LockedProps {
    reason: LockReason
}

export const Locked = observer((
    {reason}: LockedProps
) => {
    const appState = useAppState();
    const activeModal = appState.topModal;

    const handleBack = useCallback(() => {
        switch (reason) {
            case "confirm_resend_limit":
            case "login_limit":
                appState.setModal({name: MODALS.SIGN_UP,  params: {step: 2}});
                break;
            case "pwd_reset_resend_limit":
                appState.setModal({name: MODALS.FORGOT_PASSWORD, params: undefined});
                break;
        }
    }, []);

    useEffect(() => {
        inAppEventsHub.subscribe('AuthBack', handleBack);
        return () => {
            inAppEventsHub.unsubscribe('AuthBack', handleBack);
        }
    }, [handleBack]);

    return (
        <AuthModal
            isOpen={activeModal === MODALS.LOCKED}
            onClose={() => {
            }}
            isBackVisible={true}
        >
            <div>
                <AuthModalHeader
                    title={intl.get('locked.header').d('Try again later')}
                />
                <LockReasonMessage reason={reason}/>
                <Button
                    type="primary"
                    className="Auth-locked-ok-btn"
                    htmlType="submit"
                    fullWidth
                    size={'lg'}
                    text={intl.get('common.ok').d('Ok')}
                    onClick={handleBack}
                />
            </div>
        </AuthModal>
    );
})

interface LockReasonMessageProps {
    reason: LockReason
}

const LockReasonMessage = ({reason}: LockReasonMessageProps) => {
    switch (reason) {
        case 'login_limit':
            return (
                <>
                    <div className="AuthModalBodyStart body-large">
                        {intl.get('locked.login_message_sum').d('Your account is temporarily locked due to exceeding the limit for confirmation code requests.')}
                    </div>
                    <div className="body-large">
                        {intl.get('locked.login_message_details').d('After waiting, if you still do not remember your password, select "Forgot password" and follow the prompts to reset your password. Repeated failed login attempts will trigger a longer account lockout.')}
                    </div>
                </>
            )
        case 'confirm_resend_limit':
        case 'pwd_reset_resend_limit':
        default:
            return (
                <div className="AuthModalBodyStart body-large">
                    {intl.get('locked.resend_message_sum').d(`You have exceeded the limit for confirmation code requests. Please try again later, and if the issue persists, don't hesitate to contact us for assistance.`)}
                </div>
            )
    }
}
